import React, { useEffect, useState } from 'react';

interface NavbarProps {
  theme: string;
  onToggleTheme: () => void;
}

const NAV_LINKS = [
  { href: '#platform', label: 'Platform' },
  { href: '#discovery', label: 'Discovery' },
  { href: '#pipeline', label: 'Pipeline' },
  { href: '#esg', label: 'ESG' },
  { href: '#recognitions', label: 'Recognitions' },
  { href: '#founders', label: 'Founders' },
];

const linkClass = (active: boolean) =>
  `text-[10px] lg:text-[11px] font-black uppercase tracking-widest transition-colors ${
    active ? 'text-brandTeal' : 'text-gray-600 dark:text-gray-300 hover:text-brandTeal'
  }`;

export const Navbar = ({ theme, onToggleTheme }: NavbarProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const ids = [...NAV_LINKS.map((link) => link.href.slice(1)), 'connect', 'top'];
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((section): section is HTMLElement => Boolean(section));

    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(`#${entry.target.id}`);
        });
      },
      { rootMargin: '-40% 0px -55% 0px' }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const shellClass =
    scrolled || menuOpen
      ? 'bg-white/90 dark:bg-brandDark/90 backdrop-blur-md border-b border-gray-100 dark:border-gray-800 shadow-sm'
      : 'bg-transparent border-b border-transparent';

  const themeButton = (extraClass: string) => (
    <button
      type="button"
      onClick={onToggleTheme}
      className={`rounded-full text-gray-700 dark:text-gray-200 hover:text-brandTeal dark:hover:text-brandTeal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brandTeal ${extraClass}`}
      aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
    >
      {theme === 'light' ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      ) : (
        <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 9h-1m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )}
    </button>
  );

  return (
    <nav
      aria-label="Section navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${shellClass}`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <a
          href="#top"
          onClick={() => setMenuOpen(false)}
          className="flex items-start shrink-0 group"
          aria-label="MatterAlchemy — back to top"
        >
          <span className="text-base md:text-lg font-black uppercase tracking-tighter text-gray-900 dark:text-white group-hover:text-brandTeal transition-colors">
            Matter<span className="text-brandTeal">Alchemy</span>
          </span>
          <span className="text-[9px] font-bold text-gray-400 dark:text-gray-500 ml-0.5 -translate-y-1 select-none">™</span>
        </a>

        <div className="hidden md:flex items-center gap-5 lg:gap-8">
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href} className={linkClass(active === link.href)}>
              {link.label}
            </a>
          ))}
          <a
            href="#connect"
            className={`text-[10px] lg:text-[11px] font-black uppercase tracking-widest px-4 py-2.5 rounded-full border transition-colors ${
              active === '#connect'
                ? 'bg-brandTeal text-white border-brandTeal'
                : 'border-brandTeal text-brandTeal hover:bg-brandTeal hover:text-white'
            }`}
          >
            Contact
          </a>
          {themeButton('p-2')}
        </div>

        <div className="flex md:hidden items-center gap-1">
          {themeButton('p-2')}
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="p-2 rounded-full text-gray-700 dark:text-gray-200 hover:text-brandTeal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brandTeal"
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div id="mobile-nav" className="md:hidden border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-brandDark">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col">
            {[...NAV_LINKS, { href: '#connect', label: 'Contact' }].map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className={`py-3 border-b border-gray-50 dark:border-gray-800/60 last:border-b-0 text-xs font-black uppercase tracking-widest transition-colors ${
                  active === link.href ? 'text-brandTeal' : 'text-gray-700 dark:text-gray-200 hover:text-brandTeal'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};
